/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import React, { useEffect, useState, ChangeEvent, KeyboardEvent, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Tooltip } from '@mui/material';
import {
    StyledChatInputWrapper,
    StyledChatInputField,
    StyledInputField,
    StyledReferenceBanner,
    StyledReferenceDismiss,
} from './ChatInput.styled';
import PaperPlane from 'assets/icons/PaperPlane';
import { Sender } from '../../../IndividualChatBodyNew';
import api from 'api';
import { ChatReference } from 'api/rest/chat_new';
import { useTranslation } from 'react-i18next';

interface Props {
    setMessages: React.Dispatch<React.SetStateAction<any[]>>;
    room: string;
    chatUserIds: number[];
    hasAccess?: boolean;
    initialMessage?: string;
    references?: ChatReference[];
}

export default function ChatInput(props: Props) {
    const [inputValue, setInputValue] = useState<string>('');
    const [activeRefs, setActiveRefs] = useState<ChatReference[]>([]);
    const [fetchedHasAccess, setFetchedHasAccess] = useState<boolean>(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { id: userId, language_setting: language } = useSelector(
        (state: RootState) => state.user.entity
    );
    const { setMessages, room, hasAccess, initialMessage, references } = props;

    useEffect(() => {
        if (references && references.length > 0) {
            setActiveRefs(references);
        } else if (initialMessage) {
            setInputValue(initialMessage);
        }
    }, [initialMessage, references]);
    const shouldFetchAccess = hasAccess === undefined;
    const effectiveHasAccess = hasAccess !== undefined ? hasAccess : fetchedHasAccess;
    const generateTempId = () => `temp-${Date.now()}`;

    useEffect(() => {
        if (!shouldFetchAccess || !room) {
            return;
        }
        let isCancelled = false;
        (async () => {
            try {
                const res = await api.chatNew.has_access(parseInt(room));
                if (!isCancelled) {
                    setFetchedHasAccess(Boolean(res.data?.has_access));
                }
            } catch {
                if (!isCancelled) {
                    setFetchedHasAccess(false);
                }
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [room, shouldFetchAccess]);

    useEffect(() => {
        if (!room) {
            return;
        }

        let lastPolledAt = new Date().toISOString();
        let requestInFlight = false;

        const interval = setInterval(async () => {
            if (requestInFlight) {
                return;
            }

            requestInFlight = true;
            const pollStartedAt = new Date().toISOString();

            try {
                const res = await api.chatNew.poll_messages(parseInt(room), lastPolledAt);
                const newMessages: any[] = res.data;
                if (newMessages.length > 0) {
                    setMessages((prevMessages) => {
                        const existingIds = new Set(
                            prevMessages
                                .map((message) => message.id)
                                .filter((id) => id !== undefined && id !== null)
                        );
                        const otherUserMessages = newMessages.filter((msg) => {
                            if (msg.user_id === userId || existingIds.has(msg.id)) {
                                return false;
                            }

                            existingIds.add(msg.id);
                            return true;
                        });

                        if (otherUserMessages.length === 0) {
                            return prevMessages;
                        }

                        return [
                            ...prevMessages,
                            ...otherUserMessages.map((msg) => ({
                                id: msg.id,
                                sender: msg.user_id === null ? Sender.ADMIN : Sender.OTHER,
                                message: msg.text,
                                createdAt: msg.created_at,
                                user: msg.user,
                                read: msg.read,
                                references: msg.references,
                            })),
                        ];
                    });
                }
                lastPolledAt = pollStartedAt;
            } catch (error) {
                console.error('Polling error:', error);
            } finally {
                requestInFlight = false;
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [userId, room, setMessages]);

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
        autoResizeTextarea();
    };

    const handleSendMessage = async () => {
        if (inputValue.trim() !== '') {
            const message = inputValue.trim();
            const tempId = generateTempId();
            const refsToSend = activeRefs.length > 0 ? activeRefs : undefined;
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    sender: Sender.ME,
                    message: message,
                    createdAt: new Date().toISOString(),
                    tempId: tempId,
                    references: refsToSend,
                },
            ]);
            setInputValue('');
            setActiveRefs([]);
            autoResizeTextarea();
            try {
                const response = await api.chatNew.send_message(
                    parseInt(room),
                    message,
                    refsToSend
                );
                setMessages((prevMessages) =>
                    prevMessages.map((msg) => {
                        return msg.tempId === tempId
                            ? { ...msg, id: response.data.message_id }
                            : msg;
                    })
                );
            } catch (error) {
                console.error('Send message error:', error);
            } finally {
                autoResizeTextarea();
            }
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const isInputDisabled = !effectiveHasAccess;
    const { t } = useTranslation();
    const autoResizeTextarea = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    const handleDismissRef = (index: number) => {
        setActiveRefs((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <StyledChatInputWrapper>
            {activeRefs.map((ref, i) => (
                <StyledReferenceBanner key={i}>
                    <span>
                        Re [{ref.type}]: {ref.name || `#${ref.id}`}
                    </span>
                    <StyledReferenceDismiss onClick={() => handleDismissRef(i)}>
                        &times;
                    </StyledReferenceDismiss>
                </StyledReferenceBanner>
            ))}
            <StyledChatInputField>
                <Tooltip
                    title={
                        isInputDisabled ? t('youAreNotAMemberOfThisChat', { lng: language }) : ''
                    }
                    placement={'top'}
                >
                    <StyledInputField
                        ref={textareaRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={`${t('writeAMessage', { lng: language })}...`}
                        disabled={isInputDisabled}
                    />
                </Tooltip>
                <PaperPlane onClick={handleSendMessage} />
            </StyledChatInputField>
        </StyledChatInputWrapper>
    );
}
